import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Image, Input, message, Row, Space } from 'antd';
import Carousel from 'antd/es/carousel';
import { useSnapshot } from 'valtio';
import { editPageComponentConfigApi, getPageComponentConfigListApi } from '@/api/pageConfig';
import ImageUploader from '@/component/ImageUploader';
import { PAGE_CONFIG_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';
import styles from './index.module.scss';

const createDefaultCardConfig = () => ({
  jdImageUrl: '',
  tmImageUrl: '',
});

const createDefaultMarketingConfig = () => ({
  title: '营销活动',
  slides: [
    {
      id: `${Date.now()}`,
      title: '',
      link: '',
      jdImageUrl: '',
      tmImageUrl: '',
    },
  ],
});

const PageConfig: React.FC = () => {
  const { currentShopId, currentShopPlatformType } = useSnapshot(state);
  const [cardForm] = Form.useForm();
  const [cardConfig, setCardConfig] = useState<any>({
    ...createDefaultCardConfig(),
  });
  const [marketingConfig, setMarketingConfig] = useState<any>({
    ...createDefaultMarketingConfig(),
  });
  const [cardRecord, setCardRecord] = useState<any>({});
  const [marketingRecord, setMarketingRecord] = useState<any>({});
  const [savingCard, setSavingCard] = useState(false);
  const [savingMarketing, setSavingMarketing] = useState(false);

  useEffect(() => {
    cardForm.setFieldsValue(cardConfig);
  }, [cardConfig, cardForm]);

  useEffect(() => {
    if (!currentShopId) return;
    fetchConfig('MEMBER_CARD', setCardRecord, setCardConfig, cardForm);
    fetchConfig('MARKETING_ACTIVITY', setMarketingRecord, setMarketingConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  const fetchConfig = async (
    configType: 'MEMBER_CARD' | 'MARKETING_ACTIVITY',
    setRecord: (r: any) => void,
    setConfig: (c: any) => void,
    form?: any,
  ) => {
    try {
      const res = await getPageComponentConfigListApi({ configType, shopId: currentShopId });
      if (res?.code === 200 || res?.code === 0) {
        const rows = res.rows || res.data?.rows || [];
        const first = rows[0] || {};
        setRecord(first);
        if (first.configJson) {
          try {
            const parsed = JSON.parse(first.configJson);
            const normalized =
              configType === 'MEMBER_CARD'
                ? {
                    jdImageUrl: parsed?.jdImageUrl || '',
                    tmImageUrl: parsed?.tmImageUrl || '',
                  }
                : parsed;
            if (configType === 'MARKETING_ACTIVITY' && (!parsed.slides || !parsed.slides.length)) {
              normalized.slides = createDefaultMarketingConfig().slides;
            }
            setConfig((prev: any) => ({ ...prev, ...normalized }));
            form?.setFieldsValue(normalized);
          } catch (err) {
            console.error('parse configJson error', err);
          }
        } else {
          const nextConfig = configType === 'MEMBER_CARD' ? createDefaultCardConfig() : createDefaultMarketingConfig();
          setConfig(nextConfig);
          if (configType === 'MEMBER_CARD') form?.setFieldsValue(nextConfig);
        }
      } else {
        setRecord({});
        const nextConfig = configType === 'MEMBER_CARD' ? createDefaultCardConfig() : createDefaultMarketingConfig();
        setConfig(nextConfig);
        if (configType === 'MEMBER_CARD') form?.setFieldsValue(nextConfig);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveCard = async () => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    try {
      await cardForm.validateFields();
      const payload = {
        jdImageUrl: cardConfig.jdImageUrl,
        tmImageUrl: cardConfig.tmImageUrl,
      };
      setSavingCard(true);
      const res = await editPageComponentConfigApi({
        id: cardRecord.id,
        configType: 'MEMBER_CARD',
        configJson: JSON.stringify(payload),
        shopId: currentShopId,
      });
      if (res?.code === 200 || res?.code === 0) {
        message.success('会员卡配置已保存');
        setCardConfig(payload);
      }
    } catch (error) {
      if ((error as any)?.errorFields) return;
      console.error(error);
    } finally {
      setSavingCard(false);
    }
  };

  const handleSaveMarketing = async () => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    try {
      const payload = { ...marketingConfig };
      if (!payload.slides || !payload.slides.length) {
        message.warning('请至少保留一张营销活动图片');
        return;
      }
      setSavingMarketing(true);
      const res = await editPageComponentConfigApi({
        id: marketingRecord.id,
        configType: 'MARKETING_ACTIVITY',
        configJson: JSON.stringify(payload),
        shopId: currentShopId,
      });
      if (res?.code === 200 || res?.code === 0) {
        message.success('营销活动配置已保存');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSavingMarketing(false);
    }
  };

  const updateSlide = (id: string, patch: any) => {
    setMarketingConfig((cfg: any) => ({
      ...cfg,
      slides: cfg.slides.map((s: any) => (s.id === id ? { ...s, ...patch } : s)),
    }));
  };

  const addSlide = () => {
    const newSlide = {
      id: `${Date.now()}`,
      title: '',
      link: '',
      jdImageUrl: '',
      tmImageUrl: '',
    };
    setMarketingConfig((cfg: any) => ({ ...cfg, slides: [...cfg.slides, newSlide] }));
  };

  const removeSlide = (id: string) => {
    setMarketingConfig((cfg: any) => ({ ...cfg, slides: cfg.slides.filter((s: any) => s.id !== id) }));
  };

  const moveSlide = (id: string, direction: 'up' | 'down') => {
    setMarketingConfig((cfg: any) => {
      const index = cfg.slides.findIndex((s: any) => s.id === id);
      if (index < 0) return cfg;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= cfg.slides.length) return cfg;
      const newSlides = [...cfg.slides];
      const [item] = newSlides.splice(index, 1);
      newSlides.splice(targetIndex, 0, item);
      return { ...cfg, slides: newSlides };
    });
  };

  const previewSlides = useMemo(
    () => (Array.isArray(marketingConfig.slides) && marketingConfig.slides.length ? marketingConfig.slides : []),
    [marketingConfig.slides],
  );
  const cardImage = cardConfig.tmImageUrl || cardConfig.jdImageUrl;
  const hideMarketing = currentShopPlatformType === 'TM';

  return (
    <div className={styles.pageConfig}>
      <Row gutter={16}>
        <Col span={10}>
          <Card title="预览">
            <div className={styles.previewCardBody}>
              <div className={styles.previewSection}>
                <div className={styles.previewSectionTitle}>会员卡</div>
                <div className={styles.previewImageWrap}>
                  {cardImage ? (
                    <Image
                      src={cardImage}
                      alt="card"
                      preview={{ mask: null }}
                      className={styles.previewImage}
                    />
                  ) : (
                    <div className={styles.previewPlaceholder}>暂无图片</div>
                  )}
                </div>
              </div>
              {!hideMarketing && (
                <div className={styles.previewSection}>
                  <div className={styles.previewSectionTitle}>营销活动</div>
                  <div className={styles.carouselWrap}>
                    <Carousel
                      autoplay
                      dots={previewSlides.length > 1}
                      draggable
                      swipeToSlide>
                      {previewSlides.map((slide: any) => (
                        <div key={slide.id}>
                          <div className={styles.carouselItem}>
                            {slide.tmImageUrl || slide.jdImageUrl ? (
                              <Image
                                src={slide.tmImageUrl || slide.jdImageUrl}
                                alt={slide.title || 'banner'}
                                preview={{ mask: null }}
                                className={styles.carouselImg}
                              />
                            ) : (
                              <div className={styles.previewPlaceholder}>暂无图片</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </Carousel>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col span={14}>
          <Card
            title="会员卡设置"
            className={styles.cardSection}>
            <Form
              layout="vertical"
              form={cardForm}
              initialValues={cardConfig}
              onValuesChange={(_, all) => {
                setCardConfig((cfg: any) => ({ ...cfg, ...all }));
              }}>
              <Form.Item
                name="jdImageUrl"
                hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="tmImageUrl"
                hidden>
                <Input />
              </Form.Item>
              <Form.Item label="KV背景图">
                <Space
                  size={12}
                  align="start">
                  <ImageUploader
                    value={{ jdImageUrl: cardConfig.jdImageUrl, tmImageUrl: cardConfig.tmImageUrl }}
                    onChange={(val: any) => {
                      const nextJd = val?.jdUrl || val?.jdImageUrl || cardConfig.jdImageUrl;
                      const nextTm = val?.tmUrl || val?.tmImageUrl || cardConfig.tmImageUrl;
                      setCardConfig((cfg: any) => ({
                        ...cfg,
                        jdImageUrl: nextJd,
                        tmImageUrl: nextTm,
                      }));
                      cardForm.setFieldsValue({ jdImageUrl: nextJd, tmImageUrl: nextTm });
                    }}
                    maxSizeKB={1024 * 5}
                    sync={1}
                    width={726 / 3}
                    height={726 / 3}
                    limitWidth={726}
                    limitHeight={726}
                  />
                </Space>
              </Form.Item>
              <Form.Item>
                <Space size={12}>
                  {checkPermissions(PAGE_CONFIG_KEY.CANCEL) && (
                    <Button onClick={() => cardForm.setFieldsValue(cardConfig)}>取消</Button>
                  )}
                  {checkPermissions(PAGE_CONFIG_KEY.UPDATE) && (
                    <Button
                      type="primary"
                      loading={savingCard}
                      onClick={handleSaveCard}>
                      确认
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>
          </Card>

          {!hideMarketing && (
            <Card title="营销活动">
              <Space
                direction="vertical"
                className={styles.fullWidth}
                size={16}>
                {marketingConfig.slides.map((slide: any, index: number) => (
                  <Card
                    key={slide.id}
                    type="inner"
                    title={slide.title || `广告${index + 1}`}
                    extra={
                      <Space size={8}>
                        {checkPermissions(PAGE_CONFIG_KEY.UP) && (
                          <Button
                            type="link"
                            disabled={index === 0}
                            onClick={() => moveSlide(slide.id, 'up')}
                            style={{ padding: 0 }}>
                            上移
                          </Button>
                        )}
                        {checkPermissions(PAGE_CONFIG_KEY.DOWN) && (
                          <Button
                            type="link"
                            disabled={index === marketingConfig.slides.length - 1}
                            onClick={() => moveSlide(slide.id, 'down')}
                            style={{ padding: 0 }}>
                            下移
                          </Button>
                        )}
                        {checkPermissions(PAGE_CONFIG_KEY.DELETE) && (
                          <Button
                            type="link"
                            danger
                            disabled={marketingConfig.slides.length === 1}
                            onClick={() => removeSlide(slide.id)}
                            style={{ padding: 0 }}>
                            删除
                          </Button>
                        )}
                      </Space>
                    }>
                    <Form layout="vertical">
                      <Form.Item label="广告标题">
                        <Input
                          placeholder="请输入"
                          value={slide.title}
                          onChange={e => updateSlide(slide.id, { title: e.target.value })}
                        />
                      </Form.Item>
                      <Form.Item label="背景图">
                        <ImageUploader
                          value={{ jdImageUrl: slide.jdImageUrl, tmImageUrl: slide.tmImageUrl }}
                          onChange={(val: any) =>
                            updateSlide(slide.id, {
                              jdImageUrl: val?.jdUrl || val?.jdImageUrl || slide.jdImageUrl,
                              tmImageUrl: val?.tmUrl || val?.tmImageUrl || slide.tmImageUrl,
                            })
                          }
                          maxSizeKB={1024 * 5}
                          sync={1}
                          width={726 / 3}
                          height={346 / 3}
                          limitWidth={726}
                          limitHeight={346}
                        />
                      </Form.Item>
                      <Form.Item label="跳转链接">
                        <Input
                          placeholder="请输入"
                          value={slide.link}
                          onChange={e => updateSlide(slide.id, { link: e.target.value })}
                        />
                      </Form.Item>
                    </Form>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={addSlide}
                  block>
                  新增广告图
                </Button>
                <Space size={12}>
                  {checkPermissions(PAGE_CONFIG_KEY.CANCEL) && (
                    <Button onClick={() => fetchConfig('MARKETING_ACTIVITY', setMarketingRecord, setMarketingConfig)}>
                      取消
                    </Button>
                  )}
                  {checkPermissions(PAGE_CONFIG_KEY.UPDATE) && (
                    <Button
                      type="primary"
                      loading={savingMarketing}
                      onClick={handleSaveMarketing}>
                      确认
                    </Button>
                  )}
                </Space>
              </Space>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PageConfig;
